// Copyright (c) Bentley Systems
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActionColumn,
  Button,
  DefaultCell,
  DropdownMenu,
  IconButton,
  InformationPanel,
  InformationPanelBody,
  InformationPanelContent,
  InformationPanelHeader,
  InformationPanelWrapper,
  MenuItem,
  Table,
  TablePaginator,
  TablePaginatorRendererProps,
  Text,
} from "@itwin/itwinui-react";
import { CONTENT_TYPES, HTTP_HEADERS } from "../Helpers/http-constants";
import {
  ExternalLink,
  ExternalLinkDto,
  ExternalLinkPatchDto,
} from "./Models/external-link";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  selectMainMenuHeightWithMargin,
  selectSideMenuWidthWithMargin,
} from "../app/header-slice";
import {
  CellProps,
  CellRendererProps,
  Column,
} from "@itwin/itwinui-react/react-table";
import { selectAccessToken } from "../app/authorization-slice";
import { useToastNotificationService } from "../Helpers/ToastNotificationService";
import IconByName from "../Icons/IconByName";
import { selectConfirmation, setConfirmation } from "../app/confirmation-slice";
import TableHeaderCellItemAction from "../Elist/TableHeaderCellItemAction";
import {
  SvgArrowDown,
  SvgArrowUp,
  SvgDelete,
  SvgMore,
} from "@itwin/itwinui-icons-react";
import ExternalLinkForm from "./ExternalLinkForm";
import {
  checkInternalServerError,
  getParametersFromUrlString,
  getQueryParam,
  getUrlBasedOnEnv,
  setErrorState,
} from "../Helpers/helper-functions";
import { useElementHeights } from "../Helpers/useElementHeights";
import { useNodePermission } from "../Helpers/useNodePermission";
import { DawComponent } from "../Helpers/daw-component";
import { setformOpen } from "../app/form-open-slice";
import { useLocation, useNavigate } from "react-router-dom";

const ExternalViews: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const rawPath = location.pathname;
  const rawParams = rawPath.substring(rawPath.lastIndexOf("/") + 1);
  const headerDivRef = useRef<HTMLDivElement>(null);
  const headerButtonsDivRef = useRef<HTMLDivElement>(null);
  const [headerDivHeight, headerButtonsDivHeight] = useElementHeights(
    headerDivRef,
    headerButtonsDivRef,
  );
  const [data, setData] = useState<ExternalLinkDto[]>([]);
  const [dataLoaded, setDataLoaded] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const sideMenuWidth = useAppSelector(selectSideMenuWidthWithMargin);
  const mainMenuHeight = useAppSelector(selectMainMenuHeightWithMargin);
  const accessToken = useAppSelector(selectAccessToken);
  const { showToast } = useToastNotificationService();
  const [fRollUrl, setFrollUrl] = useState<string>();
  const confirmationState = useAppSelector(selectConfirmation);
  const [selectedExternalLink, setSelectedExternalLink] = useState<
    ExternalLink | undefined
  >(undefined);
  const [selectedRows, setSelectedRows] = useState<ExternalLinkDto[]>([]);
  const [urlParamsProcessed, setUrlParamsProcessed] = useState<boolean>(false);
  const [isSwapping, setIsSwapping] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const iTwinId = getQueryParam("iTwinId");
  const iModelId = getQueryParam("iModelId");
  const iTwinQueryParam = getQueryParam("iTwinId");
  const { isNodeAllowed: isBdpiNodeAllowed } = useNodePermission(
    DawComponent.DAW_COMPONENT_EXTERNAL_VIEWS,
    {
      enabled: !!iTwinId,
      extraHeaders: iTwinQueryParam
        ? { "Itwin-ID": iTwinQueryParam }
        : undefined,
    },
  );

  const abortControllerRef2 = useRef<AbortController | null>(null);

  const urlParameters = useMemo(
    () => getParametersFromUrlString(rawParams),
    [rawParams],
  );

  const isBulkOperationDisabled = useMemo((): boolean => {
    return selectedRows.length === 0;
  }, [selectedRows]);

  const nextIndex = useMemo((): number => {
    if (data.length === 0) {
      return 1;
    }
    return Math.max(...data.map((item) => item.index)) + 1;
  }, [data]);

  function getZindex(): number {
    return confirmationState.isConfirmationOpen ? 0 : 2;
  }

  useEffect(() => {
    if (!iTwinId) {
      showToast({
        status: "negative",
        message: "iTwinId is not provided in the URL.",
      });
    }
    if (!iModelId) {
      showToast({
        status: "negative",
        message: "iModelId is not provided in the URL.",
      });
    }
  }, [iTwinId, iModelId, showToast]);

  useEffect(() => {
    const fetchData = async () => {
      // Abort any existing request before creating new controller
      if (abortControllerRef2.current) {
        abortControllerRef2.current.abort();
      }
      abortControllerRef2.current = new AbortController();
      const runId = crypto.randomUUID();
      try {
        setLoading(true);
        const url = getUrlBasedOnEnv(
          `/api/ExternalLinks/itwins/${iTwinId}/imodels/${iModelId}`,
        );

        const response = await fetch(url, {
          method: "GET",
          headers: {
            [HTTP_HEADERS.CONTENT_TYPE]: CONTENT_TYPES.JSON,
            [HTTP_HEADERS.AUTHORIZATION]: accessToken,
            [HTTP_HEADERS.RUN_ID]: runId,
          },
          signal: abortControllerRef2.current.signal,
        });

        if (response.ok) {
          const responseData = await response.json();
          const formattedData = (responseData.externalLinks ||
            []) as ExternalLink[];
          setData(formattedData);
        } else {
          // not found status just says that there are no data for this iTwinId
          // do not show any error in this case
          if (response.status !== 404) {
            const responseData = await response.json();
            checkInternalServerError(
              response.status,
              dispatch,
              responseData.runId || runId,
            );
            setErrorState(
              response.status,
              dispatch,
              responseData.runId || runId,
            );
          }
        }
      } catch (error: any) {
        if (error?.name !== "AbortError") {
          setErrorState(500, dispatch, runId, true);
        }
      } finally {
        // Mark data as loaded regardless of success, 404, or error
        setDataLoaded(true);
        setLoading(false);
      }
    };

    if (iTwinId && iModelId && isBdpiNodeAllowed) {
      void fetchData();
    }

    return () => {
      if (abortControllerRef2.current) {
        abortControllerRef2.current.abort();
      }
    };
  }, [accessToken, iTwinId, iModelId, isBdpiNodeAllowed, dispatch]);

  useEffect(() => {
    // Handle URL parameters to open form with pre-filled data
    // Only process after data fetch is complete
    if (
      urlParameters?.length > 0 &&
      !fRollUrl &&
      !urlParamsProcessed &&
      dataLoaded
    ) {
      const projectNameParam = urlParameters.find(
        (p) => p.name === "PROJECT_NAME",
      );
      const urlParam = urlParameters.find((p) => p.name === "URL");

      if (
        projectNameParam &&
        projectNameParam.value &&
        urlParam &&
        urlParam.value
      ) {
        const newLink: ExternalLink = {
          index: nextIndex,
          caption: decodeURIComponent(projectNameParam.value),
          url: decodeURIComponent(urlParam.value),
          showInExternalViewsDialog: true,
          enabled: true,
        };

        setSelectedExternalLink(newLink);
        setFrollUrl("newExternalView");
        setUrlParamsProcessed(true);
      }
    }
  }, [
    data,
    urlParameters,
    fRollUrl,
    urlParamsProcessed,
    dataLoaded,
    nextIndex,
  ]);

  useEffect(() => {
    if (fRollUrl === undefined) {
      dispatch(setformOpen({ isOpen: false }));
    } else {
      dispatch(setformOpen({ isOpen: true }));
    }
  }, [dispatch, fRollUrl]);

  // Shared function to delete external links by IDs
  const deleteExternalLinks = useCallback(
    async (idsToDelete: number[]): Promise<boolean> => {
      try {
        setLoading(true);
        const propsRunId = crypto.randomUUID();

        // Check if deleting all items
        const isAllSelected = idsToDelete.length === data.length;

        if (isAllSelected) {
          // Delete all - use DELETE endpoint
          const deleteUrl = getUrlBasedOnEnv(
            `/api/ExternalLinks/itwins/${iTwinId}/imodels/${iModelId}/externallinks`,
          );
          const deleteResponse = await fetch(deleteUrl, {
            method: "DELETE",
            headers: {
              [HTTP_HEADERS.CONTENT_TYPE]: CONTENT_TYPES.JSON,
              [HTTP_HEADERS.AUTHORIZATION]: accessToken,
              [HTTP_HEADERS.RUN_ID]: propsRunId,
            },
          });

          if (!deleteResponse.ok) {
            const responseData = await deleteResponse.json();
            checkInternalServerError(
              deleteResponse.status,
              dispatch,
              responseData.runId || propsRunId,
            );
            showToast({
              status: "negative",
              message: `Failed to delete external links: ${deleteResponse.status}`,
            });
            return false;
          }

          // Clear local data
          setData([]);
          setSelectedRows([]);
        } else {
          // Delete selected - filter out deleted items and save remaining with PATCH
          const idsSet = new Set(idsToDelete);
          const remainingItems: ExternalLinkPatchDto[] = data
            .filter((item) => item.id !== undefined && !idsSet.has(item.id))
            .map((item) => ({
              id: item.id,
              caption: item.caption,
              url: item.url,
              showInExternalViewsDialog: item.showInExternalViewsDialog,
              enabled: item.enabled,
            }));

          const saveUrl = getUrlBasedOnEnv(
            `/api/ExternalLinks/itwins/${iTwinId}/imodels/${iModelId}/externallinks`,
          );
          const saveResponse = await fetch(saveUrl, {
            method: "PATCH",
            headers: {
              [HTTP_HEADERS.CONTENT_TYPE]: CONTENT_TYPES.JSON,
              [HTTP_HEADERS.AUTHORIZATION]: accessToken,
              [HTTP_HEADERS.RUN_ID]: propsRunId,
            },
            body: JSON.stringify({ externalLinks: remainingItems }),
          });

          if (!saveResponse.ok) {
            const responseData = await saveResponse.json();
            checkInternalServerError(
              saveResponse.status,
              dispatch,
              responseData.runId || propsRunId,
            );
            showToast({
              status: "negative",
              message: `Failed to delete external links: ${saveResponse.status}`,
            });
            return false;
          }

          const savedData = await saveResponse.json();
          const formattedData = (savedData || []) as ExternalLink[];
          setData(formattedData);
          setSelectedRows([]);
        }

        showToast({
          status: "positive",
          message:
            idsToDelete.length === 1
              ? "External link deleted successfully!"
              : `${idsToDelete.length} external link(s) deleted successfully!`,
        });

        return true;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "An unknown error occurred";
        showToast({
          status: "negative",
          message: `Failed to delete external links: ${errorMessage}`,
        });
        return false;
      } finally {
        setLoading(false);
      }
    },
    [data, iTwinId, iModelId, accessToken, dispatch, showToast],
  );

  const buttonCellTemplate = useCallback(
    (cellProps: CellProps<any>) => {
      const menuItems = (close: () => void) => {
        const menuItemStyle = {
          height: "1rem",
          width: "1rem",
        };

        return [
          <MenuItem
            key="miEdit"
            startIcon={<IconByName name={"edit"} style={menuItemStyle} />}
            onClick={() => {
              close();
              setSelectedExternalLink(cellProps.row.original);
              setFrollUrl("credentials");
            }}
          >
            Edit
          </MenuItem>,
          <MenuItem
            key="miDelete"
            startIcon={<IconByName name={"delete"} style={menuItemStyle} />}
            onClick={() => {
              close();
              dispatch(
                setConfirmation({
                  isConfirmationOpen: true,
                  onOkAction: async () => {
                    const currentExtLink = cellProps.row
                      .original as ExternalLink;
                    const externalLinkId = currentExtLink.id;

                    if (externalLinkId) {
                      await deleteExternalLinks([externalLinkId]);
                    }
                  },
                  caption: "Delete?",
                  text: "Are you sure you want to delete this external link?",
                  okText: "Delete",
                  cancelText: "Cancel",
                }),
              );
            }}
          >
            Delete
          </MenuItem>,
        ];
      };
      return (
        <>
          <DropdownMenu menuItems={menuItems}>
            <IconButton
              styleType="borderless"
              onClick={(e) => e.stopPropagation()}
              label="Actions"
            >
              <SvgMore />
            </IconButton>
          </DropdownMenu>
          <div className="becs-table-cell-shadow-left-elist" />
        </>
      );
    },
    [
      dispatch,
      deleteExternalLinks,
    ],
  );

  const columns = useMemo(() => {
    const cols: Column<Record<string, unknown>>[] = [
      {
        Header: "Index",
        accessor: "index",
        id: "index",
        width: 130,
      },
      {
        Header: "Enabled",
        accessor: "enabled",
        id: "enabled",
        width: 130,
        cellRenderer: (props: CellRendererProps<any>) => {
          return (
            <DefaultCell
              {...props}
              style={{
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {props.cellProps.value === true ? (
                <IconByName name="StatusSuccessColor" />
              ) : (
                <IconByName name="StatusSuccessColorMuted" />
              )}
            </DefaultCell>
          );
        },
      },
      {
        Header: "Caption",
        accessor: "caption",
        id: "caption",
        width: 300,
      },
      {
        Header: "URL",
        accessor: "url",
        id: "url",
        minWidth: 250,
        cellRenderer: (props: CellRendererProps<any>) => {
          return (
            <DefaultCell {...props}>
              <a
                href={props.cellProps.value}
                target="_blank"
                rel="noopener noreferrer"
              >
                {props.cellProps.value}
              </a>
            </DefaultCell>
          );
        },
      },
      {
        Header: "Show in external views dialog",
        accessor: "showInExternalViewsDialog",
        id: "showInExternalViewsDialog",
        width: 280,
        cellRenderer: (props: CellRendererProps<any>) => {
          return (
            <DefaultCell
              {...props}
              style={{
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {props.cellProps.value === true ? (
                <IconByName name="StatusSuccessColor" />
              ) : (
                <IconByName name="StatusSuccessColorMuted" />
              )}
            </DefaultCell>
          );
        },
      },
      {
        Header: "Created",
        accessor: "created",
        id: "created",
        width: 180,
        cellRenderer: (props: CellRendererProps<any>) => {
          return (
            <DefaultCell {...props}>
              {props.cellProps.value
                ? new Date(props.cellProps.value).toLocaleString()
                : ""}
            </DefaultCell>
          );
        },
      },
      {
        Header: "Created By",
        accessor: "createdByName",
        id: "createdByName",
        width: 200,
      },
      {
        Header: "Modified",
        accessor: "modified",
        id: "modified",
        width: 180,
        cellRenderer: (props: CellRendererProps<any>) => {
          return (
            <DefaultCell {...props}>
              {props.cellProps.value
                ? new Date(props.cellProps.value).toLocaleString()
                : ""}
            </DefaultCell>
          );
        },
      },
      {
        Header: "Modified By",
        accessor: "modifiedByName",
        id: "modifiedByName",
        width: 200,
      },
      {
        Header: () => {
          return (
            <TableHeaderCellItemAction
              label={"Move up"}
              generateLeftShade={true}
            />
          );
        },
        accessor: "moveUp",
        id: "moveUp",
        disableSortBy: true,
        disableResizing: true,
        width: 100,
        sticky: "right",
        cellRenderer: (props: CellRendererProps<any>) => {
          const rowIndex = props.cellProps.row.original.index;
          return (
            <DefaultCell
              {...props}
              style={{
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {rowIndex > 1 ? (
                <IconButton
                  onClick={async () => {
                    // Prevent concurrent swap operations
                    if (isSwapping) {
                      return;
                    }

                    const updatedData = [...data];

                    // Find the current row and the previous row
                    const currentIndex = updatedData.findIndex(
                      (item) => item.index === rowIndex,
                    );
                    const previousIndex = currentIndex - 1;

                    if (previousIndex >= 0) {
                      const currentItem = updatedData[currentIndex];
                      const previousItem = updatedData[previousIndex];

                      // Save to server FIRST, then update UI on success
                      try {
                        setIsSwapping(true);
                        setLoading(true);
                        const propsRunId = crypto.randomUUID();
                        const url = getUrlBasedOnEnv(
                          `/api/ExternalLinks/itwins/${iTwinId}/imodels/${iModelId}/externallinks:swap`,
                        );
                        const response = await fetch(url, {
                          method: "PUT",
                          headers: {
                            [HTTP_HEADERS.CONTENT_TYPE]: CONTENT_TYPES.JSON,
                            [HTTP_HEADERS.AUTHORIZATION]: accessToken,
                            [HTTP_HEADERS.RUN_ID]: propsRunId,
                          },
                          body: JSON.stringify({
                            id1: currentItem.id,
                            index1: currentItem.index,
                            id2: previousItem.id,
                            index2: previousItem.index,
                          }),
                        });

                        if (!response.ok) {
                          const responseData = await response.json();
                          checkInternalServerError(
                            response.status,
                            dispatch,
                            responseData.runId || propsRunId,
                          );
                          showToast({
                            status: "negative",
                            message: `Failed to update order: ${response.status}`,
                          });
                        } else {
                          // Only update UI after server confirms success
                          // Swap the current row with the previous row
                          [
                            updatedData[currentIndex],
                            updatedData[previousIndex],
                          ] = [previousItem, currentItem];

                          // Reassign the index values
                          const reindexedData = updatedData.map(
                            (item, index) => ({
                              ...item,
                              index: index + 1,
                            }),
                          );

                          setData(reindexedData);
                        }
                      } catch (error) {
                        const errorMessage =
                          error instanceof Error
                            ? error.message
                            : "An unknown error occurred";
                        showToast({
                          status: "negative",
                          message: `Failed to reorder items: ${errorMessage}`,
                        });
                      } finally {
                        setLoading(false);
                        setIsSwapping(false);
                      }
                    }
                  }}
                  disabled={isSwapping || loading}
                  styleType="borderless"
                  label={"Move up"}
                >
                  <SvgArrowUp />
                </IconButton>
              ) : (
                <div /> // Render an empty cell
              )}

              <div className="becs-table-cell-shadow-left-elist" />
            </DefaultCell>
          );
        },
      },
      {
        Header: () => {
          return (
            <TableHeaderCellItemAction
              label={"Move down"}
              generateLeftShade={false}
            />
          );
        },
        accessor: "moveDown",
        id: "moveDown",
        disableSortBy: true,
        disableResizing: true,
        width: 100,
        sticky: "right",
        cellRenderer: (props: CellRendererProps<any>) => {
          const rowIndex = props.cellProps.row.original.index;
          const totalRows = props.cellProps.rows.length; // Get the total number of rows
          return (
            <DefaultCell
              {...props}
              style={{
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {rowIndex < totalRows ? (
                <IconButton
                  onClick={async () => {
                    // Prevent concurrent swap operations
                    if (isSwapping) {
                      return;
                    }

                    const updatedData = [...data];

                    // Find the current row and the next row
                    const currentIndex = updatedData.findIndex(
                      (item) => item.index === rowIndex,
                    );
                    const nextIndx = currentIndex + 1;

                    if (nextIndx < updatedData.length) {
                      const currentItem = updatedData[currentIndex];
                      const nextItem = updatedData[nextIndx];

                      // Save to server FIRST, then update UI on success
                      try {
                        setIsSwapping(true);
                        setLoading(true);
                        const propsRunId = crypto.randomUUID();
                        const url = getUrlBasedOnEnv(
                          `/api/ExternalLinks/itwins/${iTwinId}/imodels/${iModelId}/externallinks:swap`,
                        );
                        const response = await fetch(url, {
                          method: "PUT",
                          headers: {
                            [HTTP_HEADERS.CONTENT_TYPE]: CONTENT_TYPES.JSON,
                            [HTTP_HEADERS.AUTHORIZATION]: accessToken,
                            [HTTP_HEADERS.RUN_ID]: propsRunId,
                          },
                          body: JSON.stringify({
                            id1: currentItem.id,
                            index1: currentItem.index,
                            id2: nextItem.id,
                            index2: nextItem.index,
                          }),
                        });

                        if (!response.ok) {
                          const responseData = await response.json();
                          checkInternalServerError(
                            response.status,
                            dispatch,
                            responseData.runId || propsRunId,
                          );
                          showToast({
                            status: "negative",
                            message: `Failed to update order: ${response.status}`,
                          });
                        } else {
                          // Only update UI after server confirms success
                          // Swap the current row with the next row
                          [updatedData[currentIndex], updatedData[nextIndx]] = [
                            nextItem,
                            currentItem,
                          ];

                          // Reassign the index values
                          const reindexedData = updatedData.map(
                            (item, index) => ({
                              ...item,
                              index: index + 1,
                            }),
                          );

                          setData(reindexedData);
                        }
                      } catch (error) {
                        const errorMessage =
                          error instanceof Error
                            ? error.message
                            : "An unknown error occurred";
                        showToast({
                          status: "negative",
                          message: `Failed to reorder items: ${errorMessage}`,
                        });
                      } finally {
                        setLoading(false);
                        setIsSwapping(false);
                      }
                    }
                  }}
                  disabled={isSwapping || loading}
                  styleType="borderless"
                  label={"Move down"}
                >
                  <SvgArrowDown />
                </IconButton>
              ) : (
                <div /> // Render an empty cell
              )}
            </DefaultCell>
          );
        },
      },
      {
        ...ActionColumn({ columnManager: true }),
        Cell: buttonCellTemplate,
        sticky: "right",
      },
    ];
    return cols;
  }, [
    buttonCellTemplate,
    data,
    iTwinId,
    iModelId,
    accessToken,
    dispatch,
    showToast,
    isSwapping,
    loading,
  ]);

  const pageSizeList = useMemo(() => [10, 25, 50, 100], []);
  const paginator = useCallback(
    (props: TablePaginatorRendererProps) => (
      <TablePaginator
        {...props}
        pageSizeList={pageSizeList}
        isLoading={false}
      />
    ),
    [pageSizeList],
  );

  const refreshListFunction = async () => {
    // Reload data from server to get the latest state
    try {
      setLoading(true);
      const url = getUrlBasedOnEnv(
        `/api/ExternalLinks/itwins/${iTwinId}/imodels/${iModelId}`,
      );
      const propsRunId = crypto.randomUUID();
      const response = await fetch(url, {
        method: "GET",
        headers: {
          [HTTP_HEADERS.CONTENT_TYPE]: CONTENT_TYPES.JSON,
          [HTTP_HEADERS.AUTHORIZATION]: accessToken,
          [HTTP_HEADERS.RUN_ID]: propsRunId,
        },
      });

      if (response.ok) {
        const responseData = await response.json();
        const formattedData = (responseData.externalLinks ||
          []) as ExternalLink[];
        setData(formattedData);
      } else {
        // Reload failed - show warning but keep current data
        showToast({
          status: "warning",
          message:
            "Failed to refresh list. Please refresh the page to see latest data.",
        });
      }
    } catch (error) {
      // Reload failed - show warning but keep current data
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred";
      showToast({
        status: "warning",
        message: `Failed to refresh list: ${errorMessage}. Please refresh the page to see latest data.`,
      });
    } finally {
      setLoading(false);
    }
  };

  function onFrollClose(refreshList: boolean) {
    setFrollUrl(undefined);
    setSelectedExternalLink(undefined);

    // Remove URL parameters PROJECT_NAME and URL from the path
    const currentPath = location.pathname;
    const lastSlash = currentPath.lastIndexOf("/");
    const pathBase = currentPath.substring(0, lastSlash + 1);
    const params = currentPath.substring(lastSlash + 1);

    if (params.includes("PROJECT_NAME") || params.includes("URL=")) {
      // Remove PROJECT_NAME and URL parameters
      const urlParams = getParametersFromUrlString(params);
      const filteredParams = urlParams.filter(
        (p) => p.name !== "PROJECT_NAME" && p.name !== "URL",
      );

      // Rebuild URL without those parameters
      const newParams = filteredParams
        .map((p) => `${p.name}=${p.value}`)
        .join("&");
      const newPath = pathBase + (newParams || "external-views");
      void navigate(newPath + location.search, { replace: true });
    }

    if (refreshList) {
      void refreshListFunction();
    }
  }

  const onBulkDelete = async (): Promise<void> => {
    dispatch(
      setConfirmation({
        isConfirmationOpen: true,
        onOkAction: async () => {
          const idsToDelete = selectedRows
            .map((row) => row.id)
            .filter((id): id is number => id !== undefined);
          await deleteExternalLinks(idsToDelete);
        },
        caption: "Delete?",
        text: `Are you sure you want to delete ${selectedRows.length === data.length ? "all" : selectedRows.length} external link(s)?`,
        okText: "Delete",
        cancelText: "Cancel",
      }),
    );
  };

  return (
    <div style={{ height: "100%" }}>
      <InformationPanelWrapper
        style={{
          height: "100%",
          maxWidth: "100%",
          borderRadius: 0,
        }}
      >
        <div>
          {fRollUrl !== undefined && <div className="overlay" />}
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div
              style={
                fRollUrl !== undefined
                  ? { pointerEvents: "none", opacity: "0.8" }
                  : {}
              }
            >
              <div className="becs-infopanelbody-footer-line">
                <div className="becs-all-margin" ref={headerDivRef}>
                  <div className="rowWrap">
                    {/* <Action action={headerBackAction} /> */}
                    <Text variant="title">{"External views"}</Text>
                  </div>
                </div>
              </div>

              <div className="becs-ltr-margin">
                <div>
                  <div
                    className="horizontal becs-tb-margin"
                    ref={headerButtonsDivRef}
                  >
                    <div className="leftAlign">
                      <div className="rowWrap">
                        <Button
                          key="newViewBtn"
                          as="button"
                          disabled={!iTwinId || !iModelId}
                          onClick={() => {
                            setSelectedExternalLink(undefined);
                            setFrollUrl("newExternalView");
                          }}
                          styleType="high-visibility"
                        >
                          <div>New</div>
                        </Button>
                        <Button
                          key="chooseFromCatalogBtn"
                          as="button"
                          disabled={!iTwinId || !iModelId}
                          onClick={() => {
                            setSelectedExternalLink(undefined);
                            const url =
                              `/eform/170/ELF_iModelID=ELF_iModelID&iModelID=${iModelId}&iTwinId=${iTwinId}${ 
                              window.location.search}`;
                            void navigate(url);
                          }}
                          styleType="high-visibility"
                        >
                          <div>Choose from catalog</div>
                        </Button>
                        <IconButton
                          label="Delete"
                          onClick={onBulkDelete}
                          disabled={isBulkOperationDisabled}
                        >
                          <SvgDelete />
                        </IconButton>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    maxWidth: `calc(100vw - ${sideMenuWidth}px)`,
                  }}
                >
                  <Table
                    emptyTableContent="No data."
                    data={data}
                    columns={columns}
                    isLoading={loading}
                    style={{
                      height: `calc(100vh - ${mainMenuHeight + headerButtonsDivHeight + headerDivHeight}px)`,
                      width: `calc(100vw - ${sideMenuWidth}px)`,
                    }}
                    isSortable={false}
                    isResizable={false}
                    density="extra-condensed"
                    styleType="zebra-rows"
                    paginatorRenderer={paginator}
                    pageSize={25}
                    initialState={{
                      sortBy: [{ id: "index", desc: false }],
                    }}
                    isSelectable={true}
                    onSelect={(rows) => {
                      // Use setTimeout to defer state updates to avoid React warnings about state changes during rendering
                      setTimeout(() => {
                        const selected = (rows ?? []) as ExternalLink[];
                        setSelectedRows(selected);
                      }, 0);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        {fRollUrl !== undefined && (
          <InformationPanel
            resizable
            isOpen={fRollUrl !== undefined}
            style={{
              width: "40%",
              maxWidth: "70%",
              minWidth: "10%",
              zIndex: getZindex(),
            }}
            className="iui-infoPanel"
          >
            <InformationPanelHeader
              style={{
                padding: 0,
              }}
            >
              <div className="becs-all-margin">
                <Text variant="title">{"External link"}</Text>
              </div>
            </InformationPanelHeader>
            <InformationPanelBody style={{ padding: 0 }}>
              <InformationPanelContent
                displayStyle="default"
                style={{ padding: 0, height: "100%" }}
              >
                {iTwinId && iModelId && (
                  <ExternalLinkForm
                    closeFrollFunction={onFrollClose}
                    externalLink={selectedExternalLink}
                    index={nextIndex}
                    iTwinId={iTwinId}
                    iModelId={iModelId}
                  />
                )}
              </InformationPanelContent>
            </InformationPanelBody>
          </InformationPanel>
        )}
      </InformationPanelWrapper>
    </div>
  );
};

export default ExternalViews;
